import { $createCommandMenuQueryNode, $isCommandMenuQueryNode, } from '#pplx/frontend/composer-sdk/lexical/nodes/CommandMenuQueryNodeSupport.js';
import { $createParagraphNode, $createTextNode, $getRoot, $getSelection, $isElementNode, $isRangeSelection, $isRootNode, $isTextNode, } from 'lexical';
/**
 * Marker mechanics for the programmatically opened command menu.
 *
 * The menu's open state lives in the editor itself as an invisible
 * `CommandMenuQueryNode` (`getTextContent() === ''`): marker present means the
 * menu is open. The editor holds at most one marker at a time —
 * `$insertCommandMenuMarkerAtSelection` is the only insertion path and no-ops
 * while one exists, and `useMarkerAnchoredCommandMenu` strips any marker that
 * an undo/redo snapshot resurrects. The marker always sits directly after the
 * `TextNode` holding the query (or alone when opened at an empty position),
 * with the caret kept at the query's end; `commandMenuQuery.ts` derives the
 * query from it.
 */
/** Tag for marker bookkeeping updates, so the update listener can skip its own writes. */
export const COMMAND_MENU_MARKER_UPDATE_TAG = 'command-menu-marker-update';
function $getCommandMenuMarkers() {
    const markers = [];
    const visit = (node) => {
        if ($isCommandMenuQueryNode(node)) {
            markers.push(node);
            return;
        }
        if ($isElementNode(node)) {
            node.getChildren().forEach(visit);
        }
    };
    visit($getRoot());
    return markers;
}
export function $getCommandMenuMarker() {
    return $getCommandMenuMarkers()[0] ?? null;
}
/**
 * Remove the marker, closing the menu. Sweeps the whole tree so a state
 * restore that slipped an extra marker past the insertion guard cannot leave
 * one behind.
 */
export function $removeCommandMenuMarker() {
    const markers = $getCommandMenuMarkers();
    markers.forEach((marker) => marker.remove());
    return markers.length > 0;
}
function $collapseSelectionToFocus(selection) {
    if (selection.isCollapsed()) {
        return true;
    }
    const focusNode = selection.focus.getNode();
    if (!$isTextNode(focusNode) && !$isElementNode(focusNode)) {
        return false;
    }
    focusNode.select(selection.focus.offset, selection.focus.offset);
    return true;
}
function $getInsertionSelection() {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
        return selection;
    }
    // No editor selection yet (e.g. the menu opened from a button before the
    // input was ever focused): open at the end of the draft.
    $getRoot().selectEnd();
    const fallbackSelection = $getSelection();
    return $isRangeSelection(fallbackSelection) ? fallbackSelection : null;
}
function $insertMarkerAfterTextNode({ placeholderText, queryStartOffset, textNode, }) {
    textNode.insertAfter($createCommandMenuQueryNode({
        placeholderText,
        queryStartOffset,
        showPlaceholder: true,
        source: 'manual',
    }));
    textNode.selectEnd();
}
/**
 * Insert a marker at the caret (splitting the anchor text node when the caret
 * sits inside it), so the query starts exactly where the user was typing.
 * A no-op when a marker already exists.
 */
export function $insertCommandMenuMarkerAtSelection(placeholderText) {
    if ($getCommandMenuMarker()) {
        return true;
    }
    const selection = $getInsertionSelection();
    if (!selection || !$collapseSelectionToFocus(selection)) {
        return false;
    }
    const collapsedSelection = $getSelection();
    if (!$isRangeSelection(collapsedSelection)) {
        return false;
    }
    const anchorNode = collapsedSelection.anchor.getNode();
    const insertionOffset = collapsedSelection.anchor.offset;
    if ($isTextNode(anchorNode)) {
        let textNodeBeforeQuery = anchorNode;
        if (insertionOffset === 0) {
            textNodeBeforeQuery = $createTextNode('');
            anchorNode.insertBefore(textNodeBeforeQuery);
        }
        else if (insertionOffset < anchorNode.getTextContentSize()) {
            const splitNode = anchorNode.splitText(insertionOffset)[0];
            if (!splitNode) {
                return false;
            }
            textNodeBeforeQuery = splitNode;
        }
        $insertMarkerAfterTextNode({
            placeholderText,
            queryStartOffset: insertionOffset,
            textNode: textNodeBeforeQuery,
        });
        return true;
    }
    if (!$isElementNode(anchorNode)) {
        return false;
    }
    const textNodeBeforeQuery = $createTextNode('');
    if ($isRootNode(anchorNode)) {
        const paragraphNode = $createParagraphNode();
        const childAtOffset = anchorNode.getChildAtIndex(insertionOffset);
        if (childAtOffset) {
            childAtOffset.insertBefore(paragraphNode);
        }
        else {
            anchorNode.append(paragraphNode);
        }
        paragraphNode.append(textNodeBeforeQuery);
    }
    else {
        const childAtOffset = anchorNode.getChildAtIndex(insertionOffset);
        if (childAtOffset) {
            childAtOffset.insertBefore(textNodeBeforeQuery);
        }
        else {
            anchorNode.append(textNodeBeforeQuery);
        }
    }
    $insertMarkerAfterTextNode({
        placeholderText,
        queryStartOffset: 0,
        textNode: textNodeBeforeQuery,
    });
    return true;
}
//# sourceMappingURL=commandMenuMarker.js.map